import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, X, Loader2, TreePine, Search, Eye } from 'lucide-react';
import api from '../../utils/api';
import usePermission from '../../hooks/usePermission';
import { fmt4, rowCft } from '../../utils/cft';

// CFT page → Wood Types: each wood with its thickness categories (stock is kept per
// thickness, plus ALL CFT) and its standard sizes (Label, L, W, T in mm).
// The sizes fill the Issue rows of a new CFT sheet and the rows of a Wood Inward entry.
export default function WoodTypesTab() {
  const { canWrite, canEdit, canDelete } = usePermission('cft');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(null);       // { id?, name, remarks, is_active, sizes[] , view? }

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/wood-types'); setRows(r.data.rows || []); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async w => {
    if (!window.confirm(`Delete wood type "${w.name}"?`)) return;
    try { const r = await api.delete(`/wood-types/${w.id}`); toast.success(r.data.message); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };
  const shown = rows.filter(w => `${w.name} ${w.remarks || ''} ${w.sizes.map(s => s.label).join(' ')}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 border border-gray-200 bg-white rounded-lg px-2.5 py-1.5 w-full sm:w-72">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search wood or size label" className="flex-1 min-w-0 text-sm outline-none" />
        </span>
        {canWrite && <button onClick={() => setForm({ name: '', remarks: '', is_active: true, thicknesses: [25, 38, 50].map(t => ({ thickness: t, is_active: 1 })), sizes: [blankSize()] })}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"><Plus className="w-4 h-4" /> New Wood Type</button>}
      </div>

      {loading ? <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
        : !shown.length ? <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">No wood types yet{canWrite ? ' — add one with its standard sizes' : ''}</div>
        : (
          <div className="grid gap-3 md:grid-cols-2">
            {shown.map(w => (
              <div key={w.id} className={`bg-white rounded-xl border p-4 ${w.is_active ? 'border-gray-200' : 'border-gray-200 opacity-70'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold text-gray-800 flex items-center gap-2"><TreePine className="w-4 h-4 text-green-700" /> {w.name}
                      {!w.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500">Inactive</span>}</div>
                    {w.remarks && <div className="text-xs text-gray-500 mt-0.5">{w.remarks}</div>}
                    <div className="text-[11px] text-gray-400 mt-0.5">Used in {w.usage?.sheets || 0} CFT sheet(s) · {w.usage?.inwards || 0} inward(s)</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setForm({ ...w, view: true, sizes: w.sizes.map(s => ({ ...s, _k: `s${s.id}` })) })} title="View" className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Eye className="w-4 h-4" /></button>
                    {canEdit && <button onClick={() => setForm({ ...w, is_active: !!w.is_active, sizes: w.sizes.map(s => ({ ...s, _k: `s${s.id}` })) })} title="Edit" className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600"><Edit className="w-4 h-4" /></button>}
                    {canDelete && <button onClick={() => del(w)} title="Delete" className="p-1.5 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase text-gray-400 mr-0.5">Thickness</span>
                  {(w.thicknesses || []).map(t => (
                    <span key={t.thickness} className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${t.is_active ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-gray-50 border-gray-200 text-gray-400 line-through'}`}>{t.thickness} mm</span>
                  ))}
                  <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-purple-50 border-purple-200 text-purple-800">ALL CFT</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {w.sizes.length ? w.sizes.map(s => (
                    <span key={s.id} className="px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                      {s.label ? <b>{s.label} </b> : null}{Number(s.length)}×{Number(s.width)}×{Number(s.thickness)} mm
                    </span>
                  )) : <span className="text-xs text-gray-400">No standard sizes</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      {form && <WoodTypeForm form={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

const blankSize = () => ({ _k: Math.random().toString(36).slice(2), label: '', length: '', width: '', thickness: '' });

function WoodTypeForm({ form, onClose, onSaved }) {
  const [f, setF] = useState({ ...form, thicknesses: (form.thicknesses || []).map(t => ({ thickness: Number(t.thickness), is_active: t.is_active ? 1 : 0 })) });
  const [saving, setSaving] = useState(false);
  const [newT, setNewT] = useState('');
  const addThickness = () => {
    const t = Math.round(Number(newT) * 1000) / 1000;
    if (!(t > 0)) return toast.error('Enter a thickness above 0 mm');
    if (f.thicknesses.some(x => x.thickness === t)) return toast.error(`${t} mm is already in the list`);
    setF(x => ({ ...x, thicknesses: [...x.thicknesses, { thickness: t, is_active: 1 }].sort((a, b) => a.thickness - b.thickness) }));
    setNewT('');
  };
  const sizeTs = new Set(f.sizes.map(s => Number(s.thickness)).filter(t => t > 0));
  const ro = !!form.view;
  const setSize = (k, patch) => setF(x => ({ ...x, sizes: x.sizes.map(s => (s._k === k ? { ...s, ...patch } : s)) }));
  const save = async () => {
    if (!String(f.name || '').trim()) return toast.error('Enter the wood name');
    const sizes = f.sizes.filter(s => s.label || s.length || s.width || s.thickness);
    if (sizes.some(s => !(Number(s.length) > 0 && Number(s.width) > 0 && Number(s.thickness) > 0))) return toast.error('Every size needs L, W and T above 0');
    setSaving(true);
    try {
      const body = { name: f.name, remarks: f.remarks, is_active: f.is_active !== false, sizes, thicknesses: f.thicknesses };
      const r = f.id ? await api.put(`/wood-types/${f.id}`, body) : await api.post('/wood-types', body);
      toast.success(r.data.message); onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{ro ? f.name : f.id ? `Edit — ${form.name}` : 'New Wood Type'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-gray-500">Wood Name *<input disabled={ro} className="winp mt-1" value={f.name} maxLength={150} onChange={e => setF({ ...f, name: e.target.value })} placeholder="e.g. Teak, Sheesham, White Ash" /></label>
            <label className="text-xs font-semibold text-gray-500">Remarks<input disabled={ro} className="winp mt-1" value={f.remarks || ''} maxLength={500} onChange={e => setF({ ...f, remarks: e.target.value })} /></label>
          </div>
          {f.id && !ro && (
            <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={f.is_active !== false} onChange={e => setF({ ...f, is_active: e.target.checked })} /> Active (shown in CFT sheets and Wood Inward)</label>
          )}
          <div>
            <span className="text-xs font-bold text-gray-600 uppercase">Thickness Categories</span>
            <p className="text-[11px] text-gray-400 mb-1.5">Stock is kept per thickness. Issue and Return rows of a CFT sheet and Wood Inward rows pick from this list. ALL CFT is always there, for wood whose board sizes aren't known.</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {f.thicknesses.map(t => (
                <span key={t.thickness} className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md text-xs font-semibold border ${t.is_active ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                  {t.thickness} mm{!t.is_active && ' (inactive)'}
                  {!ro && <button type="button" title={t.is_active ? 'Make inactive' : 'Make active'} onClick={() => setF(x => ({ ...x, thicknesses: x.thicknesses.map(z => (z.thickness === t.thickness ? { ...z, is_active: z.is_active ? 0 : 1 } : z)) }))}
                    className="px-1 rounded text-[10px] font-bold hover:bg-white">{t.is_active ? 'Off' : 'On'}</button>}
                  {!ro && !sizeTs.has(t.thickness) && <button type="button" title="Remove" onClick={() => setF(x => ({ ...x, thicknesses: x.thicknesses.filter(z => z.thickness !== t.thickness) }))}
                    className="p-0.5 rounded hover:bg-white text-red-500"><X className="w-3 h-3" /></button>}
                </span>
              ))}
              <span className="px-2 py-0.5 rounded-md text-xs font-semibold border bg-purple-50 border-purple-200 text-purple-800">ALL CFT (always)</span>
              {!ro && (
                <span className="inline-flex items-center gap-1">
                  <input type="number" min="0" step="any" value={newT} onChange={e => setNewT(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addThickness(); } }}
                    placeholder="e.g. 60" className="w-20 px-2 py-1 border border-gray-300 rounded-md text-xs" />
                  <button type="button" onClick={addThickness} className="flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-600"><Plus className="w-3 h-3" /> mm</button>
                </span>
              )}
            </div>
            {!ro && <p className="text-[11px] text-gray-400 mt-1">A thickness already used in stock can't be removed — turn it Off instead. A standard size's thickness is added automatically.</p>}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-bold text-gray-600 uppercase">Standard Sizes ({f.sizes.length})</span>
              {!ro && <button onClick={() => setF(x => ({ ...x, sizes: [...x.sizes, blankSize()] }))} className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600"><Plus className="w-3.5 h-3.5" /> Add Size</button>}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-gray-500 bg-gray-50"><th className="px-2 py-1.5">Label</th><th className="px-2 py-1.5 text-right">L (mm)</th><th className="px-2 py-1.5 text-right">W (mm)</th><th className="px-2 py-1.5 text-right">T (mm)</th><th className="px-2 py-1.5 text-right">CFT / pc</th><th></th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {f.sizes.map(s => (
                  <tr key={s._k}>
                    <td className="px-2 py-1"><input disabled={ro} className="wcell" value={s.label || ''} onChange={e => setSize(s._k, { label: e.target.value })} placeholder="Part / plank" /></td>
                    <td className="px-2 py-1"><input disabled={ro} type="number" min="0" className="wcell num" value={s.length} onChange={e => setSize(s._k, { length: e.target.value })} /></td>
                    <td className="px-2 py-1"><input disabled={ro} type="number" min="0" className="wcell num" value={s.width} onChange={e => setSize(s._k, { width: e.target.value })} /></td>
                    <td className="px-2 py-1"><input disabled={ro} type="number" min="0" className="wcell num" value={s.thickness} onChange={e => setSize(s._k, { thickness: e.target.value })} /></td>
                    <td className="px-2 py-1 text-right text-gray-500 text-xs">{fmt4(rowCft({ ...s, qty: 1 }).gross)}</td>
                    <td className="px-1 py-1">{!ro && <button onClick={() => setF(x => ({ ...x, sizes: x.sizes.filter(z => z._k !== s._k) }))} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                  </tr>
                ))}
                {!f.sizes.length && <tr><td colSpan={6} className="px-2 py-4 text-center text-xs text-gray-400">No sizes</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {!ro && (
          <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-500 hover:bg-gray-100">Cancel</button>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
          </div>
        )}
      </div>
      <style>{`.winp{display:block;width:100%;padding:8px 10px;border:1px solid #d1d5db;border-radius:8px;font-size:13px;font-weight:400;color:#111827} .winp:disabled,.wcell:disabled{background:#f9fafb;color:#4b5563}
        .wcell{width:100%;padding:5px 7px;border:1px solid #e5e7eb;border-radius:6px;font-size:12.5px} .wcell.num{text-align:right}`}</style>
    </div>
  );
}
